import React, { useEffect } from "react";
import { StepShell } from "@/components/StepShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { autoOutcome, ishiharaScore, CONCLUSION_OPTIONS, SYMPTOMS, AMSLER_ANOMALIE } from "@/lib/screeningData";
import { getPdfPreview } from "@/lib/pdfReport";
import { useI18n, localeFor } from "@/i18n";
import { FileText, Download, RotateCcw, CheckCircle2, AlertTriangle, Eye, ChevronLeft, Gift, Lock, Loader2 } from "lucide-react";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm font-semibold text-slate-800 text-right">{value || "—"}</span>
  </div>
);

const Block = ({ title, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-6">
    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">{title}</h3>
    {children}
  </div>
);

export const Results = ({ data, update, onRestart, onBack, entitlement = {}, authorized = false, generating = false, onGenerate }) => {
  const { t, tc, lang } = useI18n();
  const outcome = autoOutcome(data);
  const { correct, total } = ishiharaScore(data);
  const p = data.patient;
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);

  // L'anteprima è pulita solo per chi ha già in mano gli asset: un utente sbloccato, o
  // questo referto una volta autorizzato. Altrimenti resta con filigrana — il visualizzatore
  // PDF del browser espone un bottone di download, quindi un'anteprima pulita sarebbe un
  // bypass completo del credito, non un dettaglio estetico.
  const clean = !!entitlement.unlocked || authorized;
  const freeRemaining = entitlement.freeRemaining;
  const hasFreeCredit = !entitlement.unlocked && freeRemaining > 0;
  const locked = !entitlement.unlocked && !authorized && freeRemaining === 0;

  const downloadLabel = entitlement.unlocked
    ? t("download_btn")
    : authorized
      ? t("btn_download_again")
      : hasFreeCredit
        ? t("btn_generate_free")
        : t("btn_generate_locked");

  const openPreview = () => { const { url } = getPdfPreview(data, lang, { clean }); setPreviewUrl(url); setPreviewOpen(true); };
  const symList = SYMPTOMS.filter((s) => data.symptoms.flags[s.key]).map((s) => t("sym_" + s.key));
  const fmt = (iso) => { if (!iso) return "—"; try { return new Date(iso + "T00:00:00").toLocaleDateString(localeFor(lang)); } catch { return iso; } };

  const rgLabel = (v) => ({ rosso: t("rg_far_red"), verde: t("rg_far_green"), uguale: t("rg_far_equal") }[v] || "—");
  const amslerLabel = (eye) => {
    if (!eye || !eye.esito) return "—";
    const an = AMSLER_ANOMALIE.filter((x) => eye.anomalie[x.key]).map((x) => t("amsan_" + x.key));
    return tc(eye.esito) + (an.length ? ` — ${an.join(", ")}` : "");
  };

  useEffect(() => {
    if (!data.conclusion.esito) update("conclusion", { esito: outcome.suggestion });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StepShell icon={FileText} title={t("res_title")} subtitle={t("res_sub")} testId="results-step">
      <div className={`rounded-xl border p-6 mb-6 flex items-start gap-4 ${
        outcome.level === "ok" ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"
      }`} data-testid="outcome-banner">
        {outcome.level === "ok"
          ? <CheckCircle2 className="w-7 h-7 text-emerald-600 flex-shrink-0" />
          : <AlertTriangle className="w-7 h-7 text-amber-600 flex-shrink-0" />}
        <div>
          <p className="text-lg font-bold text-slate-900">{tc(outcome.suggestion)}</p>
          {outcome.flags.length > 0 && (
            <ul className="mt-2 space-y-1">
              {outcome.flags.map((f, i) => <li key={i} className="text-sm text-slate-700">• {tc(f)}</li>)}
            </ul>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Block title={t("b_worker")}>
          <Row label={t("r_cognome_nome")} value={`${p.cognome} ${p.nome}`.trim()} />
          <Row label={t("r_nascita")} value={fmt(p.dataNascita)} />
          <Row label={t("r_mansione")} value={p.mansione} />
          <Row label={t("r_azienda")} value={p.reparto} />
          <Row label={t("r_correzione")} value={tc(p.correzione)} />
          <Row label={t("r_data_esame")} value={fmt(p.dataEsame)} />
          <Row label={t("r_medico")} value={p.medico} />
        </Block>

        <Block title={t("b_symptoms")}>
          {symList.length === 0 ? (
            <p className="text-sm text-slate-500">{t("r_no_symptoms")}</p>
          ) : (
            <ul className="space-y-1">{symList.map((s, i) => <li key={i} className="text-sm text-slate-700">• {s}</li>)}</ul>
          )}
          <Row label={t("r_frequenza")} value={tc(data.symptoms.frequenza)} />
        </Block>

        <Block title={t("b_acuity")}>
          <Row label={t("r_far_od")} value={tc(data.snellen.od)} />
          <Row label={t("r_far_os")} value={tc(data.snellen.os)} />
          <Row label={t("r_far_ou")} value={tc(data.snellen.ou)} />
          <Row label={t("r_near_od")} value={tc(data.near.od)} />
          <Row label={t("r_near_os")} value={tc(data.near.os)} />
        </Block>

        <Block title={t("b_contrast_color")}>
          <Row label={t("r_contrast_od")} value={tc(data.contrast.od)} />
          <Row label={t("r_contrast_os")} value={tc(data.contrast.os)} />
          <Row label={t("r_ishihara")} value={`${correct} / ${total} ${t("r_correct_of")}`} />
          <Row label={t("r_first_plate")} value={data.ishihara.responses.p1 === "yes" ? t("ish_recognized") : data.ishihara.responses.p1 === "no" ? t("ish_not_recognized") : "—"} />
          <Row label={t("r_redgreen")} value={rgLabel(data.redGreen.result)} />
        </Block>

        <Block title={t("b_amsler")}>
          <Row label={t("r_amsler_od")} value={amslerLabel(data.amsler.od)} />
          <Row label={t("r_amsler_os")} value={amslerLabel(data.amsler.os)} />
        </Block>

        <Block title={t("b_astig")}>
          <Row label={t("r_astig_od")} value={tc(data.astigmatism.od.esito)} />
          <Row label={t("r_astig_os")} value={tc(data.astigmatism.os.esito)} />
          <Row label={t("r_astig_ou")} value={tc(data.astigmatism.ou?.esito)} />
        </Block>
      </div>

      {(data.snellen.note || data.contrast.note || data.near.note || data.ishihara.note || data.symptoms.note || data.redGreen.note || data.astigmatism.note) && (
        <div className="mt-4">
          <Block title={t("b_notes")}>
            {data.symptoms.note && <Row label={t("n_symptoms")} value={data.symptoms.note} />}
            {data.snellen.note && <Row label={t("n_far")} value={data.snellen.note} />}
            {data.astigmatism.note && <Row label={t("n_astig")} value={data.astigmatism.note} />}
            {data.contrast.note && <Row label={t("n_contrast")} value={data.contrast.note} />}
            {data.near.note && <Row label={t("n_near")} value={data.near.note} />}
            {data.ishihara.note && <Row label={t("n_color")} value={data.ishihara.note} />}
            {data.redGreen.note && <Row label={t("n_redgreen")} value={data.redGreen.note} />}
          </Block>
        </div>
      )}

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4">{t("concl_title")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("esito_label")}</Label>
            <Select value={data.conclusion.esito} onValueChange={(v) => update("conclusion", { esito: v })}>
              <SelectTrigger data-testid="conclusion-esito-select"><SelectValue placeholder={t("esito_ph")} /></SelectTrigger>
              <SelectContent>
                {CONCLUSION_OPTIONS.map((o) => <SelectItem key={o} value={o}>{tc(o)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("racc_label")}</Label>
            <Textarea data-testid="conclusion-note-input" rows={3} value={data.conclusion.raccomandazioni}
              onChange={(e) => update("conclusion", { raccomandazioni: e.target.value })}
              placeholder={t("racc_ph")} />
          </div>
        </div>
      </div>

      {/* L'utente deve sapere che sta usando il credito *prima* di cliccare, non dopo. */}
      {hasFreeCredit && !authorized && (
        <div className="mt-6 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="free-report-note">
          <Gift className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-emerald-900 leading-relaxed">{t("res_free_note")}</p>
        </div>
      )}

      <div className="mt-8 mb-4 flex flex-col sm:flex-row gap-3">
        {onBack && (
          <Button size="lg" variant="outline" data-testid="results-back-button" onClick={onBack}
            className="h-12 px-6 text-base font-semibold">
            <ChevronLeft className="w-5 h-5" /> {t("back")}
          </Button>
        )}
        <Button size="lg" variant="outline" data-testid="preview-pdf-button" onClick={openPreview}
          className="h-12 px-8 text-base font-semibold">
          <Eye className="w-5 h-5" /> {t("preview_btn")}
        </Button>
        <Button size="lg" data-testid="download-pdf-button" onClick={onGenerate} disabled={generating}
          className="h-12 px-8 text-base font-semibold bg-primary hover:bg-primary/90 transition-colors">
          {generating ? <Loader2 className="w-5 h-5 animate-spin" /> : locked ? <Lock className="w-5 h-5" /> : <Download className="w-5 h-5" />}
          {downloadLabel}
        </Button>
        <Button size="lg" variant="outline" data-testid="restart-button" onClick={onRestart}
          className="h-12 px-8 text-base font-semibold">
          <RotateCcw className="w-5 h-5" /> {t("restart_btn")}
        </Button>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl w-[95vw]">
          <DialogHeader>
            <DialogTitle>{t("preview_title")}</DialogTitle>
            <DialogDescription>{clean ? t("preview_desc") : t("preview_watermark_note")}</DialogDescription>
          </DialogHeader>
          {previewUrl && (
            <iframe title={t("preview_title")} src={previewUrl} data-testid="pdf-preview-iframe"
              className="w-full h-[70vh] rounded-md border border-slate-200 bg-white" />
          )}
          <DialogFooter>
            <Button data-testid="preview-download-button" onClick={onGenerate} disabled={generating}>
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : locked ? <Lock className="w-4 h-4" /> : <Download className="w-4 h-4" />}
              {downloadLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StepShell>
  );
};
